import { invoke } from '~/lib/tauriInvoke'
import * as Sentry from '@sentry/nuxt'

const TERMINATOR = '\x1e'

export interface SignalRInvocation {
  type: number
  target?: string
  arguments?: unknown[]
}

export interface SignalRConnectionOptions {
  /** Console log prefix, e.g. `'[SignalR]'` / `'[UsersSignalR]'`. */
  label: string
  /** Absolute hub URL, e.g. `'http://.../hubs/groups'`. */
  hubUrl: string
  /** Dispatches a decoded server invocation (SignalR message type 1) to the caller's handlers. */
  onInvocation: (invocation: SignalRInvocation) => void
  /** Sentry `extra.action` tag used when the initial `connect()` throws. */
  connectErrorAction: string
  reconnectDelayMs?: number
}

/**
 * Both hub connections (groups, users) reimplemented the same raw SignalR-over-WebSocket protocol —
 * the JSON handshake, record-separator (`\x1e`) framing, ping/pong keepalive, and close-triggered
 * reconnect. Centralized here so that protocol handling only needs to be right once; each hub
 * composable now just supplies its URL and an `onInvocation` dispatcher for its own event targets.
 */
export function createSignalRConnection(options: SignalRConnectionOptions) {
  const { label, hubUrl, onInvocation, connectErrorAction, reconnectDelayMs = 5000 } = options

  const socket = ref<WebSocket | null>(null)
  const isConnected = ref(false)

  async function connect() {
    if (socket.value) return

    try {
      const token = await invoke<string>('get_access_token_bridge')
      if (!token) {
        console.error(`${label} No access token found`)
        return
      }

      const url = new URL(hubUrl)
      url.searchParams.append('access_token', token)

      const ws = new WebSocket(url.toString().replace('http', 'ws'))

      ws.onopen = () => {
        console.log(`${label} WebSocket connected`)
        // SignalR Handshake
        ws.send(JSON.stringify({ protocol: 'json', version: 1 }) + TERMINATOR)
      }

      ws.onmessage = (event) => {
        const data = event.data as string
        const messages = data.split(TERMINATOR)

        for (const msg of messages) {
          if (!msg) continue
          const payload = JSON.parse(msg)

          // Handshake response
          if (Object.keys(payload).length === 0 && !isConnected.value) {
            isConnected.value = true
            console.log(`${label} Handshake successful`)
            continue
          }

          // Ping
          if (payload.type === 6) {
            ws.send(JSON.stringify({ type: 6 }) + TERMINATOR)
            continue
          }

          // Invocation (message from server)
          if (payload.type === 1) {
            onInvocation(payload as SignalRInvocation)
          }
        }
      }

      ws.onclose = () => {
        console.log(`${label} WebSocket closed`)
        isConnected.value = false
        socket.value = null
        // Reconnect after delay if still authenticated
        setTimeout(connect, reconnectDelayMs)
      }

      ws.onerror = (err) => {
        console.error(`${label} WebSocket error:`, err)
      }

      socket.value = ws
    } catch (e) {
      console.error(`${label} Failed to connect:`, e)
      Sentry.captureException(e, { extra: { action: connectErrorAction } })
    }
  }

  function disconnect() {
    if (socket.value) {
      const ws = socket.value
      ws.onclose = null
      ws.close()
      socket.value = null
      isConnected.value = false
    }
  }

  /**
   * Calls a hub method without waiting for a result (a SignalR invocation with no `invocationId`).
   * Dropped when the socket isn't up — callers re-send their state on (re)connect instead.
   */
  function send(target: string, ...args: unknown[]): boolean {
    const ws = socket.value
    if (!ws || !isConnected.value || ws.readyState !== WebSocket.OPEN) return false
    ws.send(JSON.stringify({ type: 1, target, arguments: args }) + TERMINATOR)
    return true
  }

  // Forces a fresh connection (e.g. after joining a group by invite) so the server re-evaluates
  // which SignalR hub groups this connection should belong to — subscriptions are established at
  // connect time and don't pick up newly-joined groups on an already-open socket.
  function reconnect() {
    disconnect()
    connect()
  }

  return { socket, isConnected, connect, reconnect, disconnect, send }
}
